import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";
import { parseDueDate } from "@/lib/status";

const EMPTY = {
  title: "",
  category: "Tax",
  dueDate: "",
  status: "Pending",
  remarks: "",
};

export default function TaskFormModal({
  mode = "create",
  open,
  onOpenChange,
  onSubmit,
  submitting,
  initial,
}) {
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    if (isEdit && initial) {
      setForm({
        title: initial.title || "",
        category: initial.category || "Tax",
        dueDate: initial.dueDate || "",
        status: initial.status || "Pending",
        remarks: initial.remarks || "",
      });
    } else {
      setForm(EMPTY);
    }
    setTouched({});
  }, [open, isEdit, initial]);

  const errors = useMemo(() => {
    const errs = {};
    const title = form.title?.trim() || "";
    if (!title) errs.title = "Title is required";
    else if (title.length > 200) errs.title = "Max 200 characters";
    if (!form.dueDate) errs.dueDate = "Due date is required";
    if (!["Tax", "Secretarial"].includes(form.category))
      errs.category = "Choose a category";
    if (!["Pending", "Completed"].includes(form.status))
      errs.status = "Choose a status";
    return errs;
  }, [form]);

  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setTouched({ title: true, dueDate: true });
    if (!canSubmit) return;
    onSubmit({
      title: form.title.trim(),
      category: form.category,
      dueDate: form.dueDate,
      status: form.status,
      remarks: (form.remarks || "").trim(),
    });
  };

  const selectedDate = useMemo(
    () => parseDueDate(form.dueDate),
    [form.dueDate]
  );

  const [dateOpen, setDateOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 border-[#E2E8F0]"
        data-testid={isEdit ? TID.editModal : TID.addModal}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E8F0]">
            <DialogTitle className="text-base font-semibold text-[#0F172A]">
              {isEdit ? "Edit Compliance Task" : "New Compliance Task"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              {isEdit
                ? "Update details. Attachments remain unchanged."
                : "Track a Tax or Secretarial compliance obligation."}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 flex flex-col gap-4">
            <Field label="Title" error={touched.title && errors.title}>
              <input
                autoFocus
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                onBlur={() => setTouched((t) => ({ ...t, title: true }))}
                maxLength={200}
                placeholder="e.g., GSTR-3B Monthly Return"
                className={cn(
                  "w-full h-9 px-3 rounded-md border bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10",
                  touched.title && errors.title
                    ? "border-[#DC2626]"
                    : "border-[#E2E8F0] focus:border-[#CBD5E1]"
                )}
                data-testid={isEdit ? TID.editTitle : TID.addTitle}
              />
              <div className="text-[10px] text-[#94A3B8] text-right mt-1 tabular-nums">
                {form.title.length}/200
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger
                    className="h-9 border-[#E2E8F0]"
                    data-testid={isEdit ? TID.editCategory : TID.addCategory}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tax">Tax</SelectItem>
                    <SelectItem value="Secretarial">Secretarial</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Status">
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger
                    className="h-9 border-[#E2E8F0]"
                    data-testid={isEdit ? TID.editStatus : TID.addStatus}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field
              label="Due Date"
              error={touched.dueDate && errors.dueDate}
            >
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onClick={() =>
                      setTouched((t) => ({ ...t, dueDate: true }))
                    }
                    className={cn(
                      "w-full h-9 px-3 rounded-md border bg-white text-sm text-left flex items-center justify-between",
                      touched.dueDate && errors.dueDate
                        ? "border-[#DC2626]"
                        : "border-[#E2E8F0]",
                      !selectedDate && "text-[#94A3B8]"
                    )}
                    data-testid={isEdit ? TID.editDueDate : TID.addDueDate}
                  >
                    <span>
                      {selectedDate
                        ? format(selectedDate, "dd MMM yyyy")
                        : "Select a date"}
                    </span>
                    <CalendarIcon className="h-4 w-4 text-[#64748B]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="p-0 border-[#E2E8F0]"
                >
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => {
                      if (d) {
                        const iso = format(d, "yyyy-MM-dd");
                        setForm((f) => ({ ...f, dueDate: iso }));
                        setDateOpen(false);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label="Remarks">
              <textarea
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                maxLength={2000}
                rows={3}
                placeholder="Internal notes, follow-ups, portal reference, etc."
                className="w-full px-3 py-2 rounded-md border border-[#E2E8F0] bg-white text-sm placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10"
                data-testid={isEdit ? TID.editRemarks : TID.addRemarks}
              />
              <div className="text-[10px] text-[#94A3B8] text-right tabular-nums">
                {(form.remarks || "").length}/2000
              </div>
            </Field>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] rounded-b-lg">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#334155] hover:bg-[#F1F5F9] disabled:opacity-50"
              data-testid={isEdit ? TID.editCancel : TID.addCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="h-9 px-3.5 rounded-md bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50 inline-flex items-center gap-2"
              data-testid={isEdit ? TID.editSubmit : TID.addSubmit}
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isEdit ? "Save Changes" : "Create Task"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-[#334155]">{label}</span>
      {children}
      {error && <span className="text-[11px] text-[#DC2626]">{error}</span>}
    </label>
  );
}
